package com.neurobs.js.opentok

import com.neurobs.ccab.{CaasType, ChatClientId, OpentokTokenData}
import com.neurobs.util.{ChatConnectionStatus, ChatSession, ChatSessionFactory, ChatSessionNotify, CommunicationState, Logger}
import com.neurobs.js.dom.VideoElement

import scala.scalajs.js
import io.circe.generic.auto._

import scala.util.{Failure, Success, Try}

class OpenTokChatSession (session: Session, notify: ChatSessionNotify, localVideoElement: VideoElement)(implicit logger: Logger) extends ChatSession {
	import OpenTokChatSession._

	override def apiType: CaasType.Value = CaasType.Opentok

	private var status = ChatConnectionStatus.Connecting
	override def connectionStatus: ChatConnectionStatus.Value = status
	private def setStatus(newStatus: ChatConnectionStatus.Value, error: Option[String]): Unit = {
		if (newStatus != status) {
			status = newStatus
			notify.connectionStatusChanged(newStatus, error)
		}
	}

	override def sendMessage(message: String, messageType: String): Unit = {
		logger.debug(s"OpenTok sending: $message")
		session.signal(js.Dynamic.literal(
				data = message,
				`type` = messageType
			), { e: js.UndefOr[Error] =>
				e.toOption match {
					case Some(error) if (error != null) && error.message.nonEmpty => notify.genericError(error.message)
					case _ =>
				}
			}
		)
	}

	private var localVideoPublisher: Option[Publisher] = None
	override def isPublishingAV: Boolean = localVideoPublisher.isDefined

	private def publishLocalAV(audio: Boolean, video: Boolean): Unit = {
		if (localVideoPublisher.isEmpty) {
			val publisher = OT.initPublisher(localVideoElement.element, js.Dynamic.literal(
				fitMode = "contain",
				insertMode = "append",
				width = "100%",
				height = "100%",
				publishAudio = audio,
				publishVideo = video,
				name = adminVideoStreamName
			), handleCompletion(notify.publishAVError))
			publisher.on(`type` = "streamCreated", (e: Event) => {
				//e.preventDefault()
				val se = e.asInstanceOf[StreamEvent]
				if (se.stream.hasVideo) {
					val (width, height) = if (se.stream.videoDimensions.width <= maxLocalVideoWidth)
						(se.stream.videoDimensions.width.toDouble, se.stream.videoDimensions.height.toDouble)
					else
						(maxLocalVideoWidth, se.stream.videoDimensions.height.toDouble / se.stream.videoDimensions.width.toDouble * maxLocalVideoWidth)
					localVideoElement.setSize(width.toInt, height.toInt)
				}
				setState(state.copy(localAudio = se.stream.hasAudio, localVideo = se.stream.hasVideo))
			})
			def clearPublisher(): Unit = {
				localVideoElement.element.innerHTML = ""
				localVideoElement.setSize(widthPx = 0, heightPx = 0)
				localVideoPublisher = None
			}
			publisher.on(`type` = "streamDestroyed", (e: Event) => {
				//e.preventDefault()
				clearPublisher()
				setState(state.copy(localAudio = false, localVideo = false))
			})

			session.publish(publisher, handleCompletion { error => notify.publishAVError(error); clearPublisher() })
			localVideoPublisher = Some(publisher)
		}
	}
	override def setPublishing(audio: Boolean, video: Boolean): Unit = {
		if ((!audio) && (!video)) localVideoPublisher.foreach(session.unpublish(_))
		else localVideoPublisher match {
			case None => publishLocalAV(audio = audio, video = video)
			case Some(p) =>
				p.publishAudio(audio)
				p.publishVideo(video)
		}
	}

	override def close(): Unit = {
		// Somehow just disconnecting doesn't stop signal notifications
		// if the same OpenTok session is connected from a subsequent
		// session object
		session.off(`type` = "streamCreated")
		session.off(`type` = "streamDestroyed")
		session.off(`type` = "streamPropertyChanged")
		session.off(`type` = controllerMessageTypeSignalString)
		session.off(`type` = adminChatMessageTypeSignalString)
		session.off(`type` = "sessionDisconnected")
		session.off(`type` = "connectionDestroyed")
		session.off(`type` = "connectionCreated")

		session.disconnect()
	}

	private var subjectVolume: Double = 100
	private var subjectSubscriber: Option[Subscriber] = None
	override def setPlaybackVolume(volume: Double): Unit = {
		subjectVolume = volume
		subjectSubscriber.foreach(_.setAudioVolume(volume))
	}

	private var state: CommunicationState = CommunicationState()
	private def setState(newState: CommunicationState): Unit = {
		if (newState != state) {
			notify.communicationStateChanged(newState)
			state = newState
		}
	}

	private def handleCompletion(errorHandler: String => Unit)(e: js.UndefOr[Error]): Unit = e.toOption foreach { error =>
		if (error != null) {
			logger.error(error.message)
			errorHandler(error.message)
		}
	}
}

class OpenTokChatSessionFactory(tokenData: OpentokTokenData)(implicit logger: Logger) extends ChatSessionFactory {
	override def connect(thisClient: ChatClientId, notify: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement): Either[String, ChatSession] = {
		OpenTokChatSession(tokenData, thisClient, notify, localVideoElement, remoteVideoElement, remoteAdminVideoElement)
	}
}

object OpenTokChatSession {
	private val controllerMessageTypeString = "Controller"
	private val controllerMessageTypeSignalString = "signal:Controller"
	private val adminChatMessageTypeString = "AdminChat"
	private val adminChatMessageTypeSignalString = "signal:AdminChat"

	private val controllerConnectionData = "Controller"
	private val controllerConnectionType = "t"
	private val controllerVideoStreamName = "Controller"
	private val adminVideoStreamName = "Admin"
	private val maxLocalVideoWidth: Double = 200

	private def connectionId(c: Connection): Option[ChatClientId] = {
		io.circe.parser.decode[ChatClientId](c.data) match {
			case Right(metadata) => Some(metadata)
			case Left(_) =>
				if (c.data == controllerConnectionData) Some(ChatClientId(uniqueId = "", `type` = controllerConnectionType, user = "Controller"))
				else None
		}
	}

	def apply(tokenData: OpentokTokenData, thisClient: ChatClientId, notify: ChatSessionNotify, localVideoElement: VideoElement, remoteVideoElement: VideoElement, remoteAdminVideoElement: VideoElement)(implicit logger: Logger): Either[String, OpenTokChatSession] = {
		Try {
			val session = OT.initSession(tokenData.apiKey.toString, tokenData.sessionId, js.Dynamic.literal())

			val socket = new OpenTokChatSession(session = session, notify = notify, localVideoElement = localVideoElement)

			def handleCompletion(errorHandler: String => Unit)(e: js.UndefOr[Error]): Unit = e.toOption foreach { error =>
				if (error != null) {
					logger.error(error.message)
					errorHandler(error.message)
				}
			}

			def subscribeToStream(s: Stream, fromSubject: Boolean): Unit = {
				logger.debug(s"subscribeToStream audio: ${s.hasAudio} video: ${s.hasVideo} subject: $fromSubject")
				val element = if (fromSubject) remoteVideoElement else remoteAdminVideoElement
				if (fromSubject) {
					remoteVideoElement.element.innerHTML = ""
					socket.setState(socket.state.copy(remoteVideo = s.hasVideo, remoteAudio = s.hasAudio))
				}
				else {
					socket.setPublishing(audio = false, video = false)
					remoteAdminVideoElement.setSize(widthPx = 150, heightPx = 150)
				}
				if (s.hasVideo) {
					element.adjustRatio(ratioWidth = s.videoDimensions.width, ratioHeight = s.videoDimensions.height)
				}
				val subscriber = session.subscribe(s, element.element, js.Dynamic.literal(
					fitMode = "contain",
					insertMode = "append",
					width = "100%",
					height = "100%",
					audioVolume = if (fromSubject) socket.subjectVolume else 100
				), handleCompletion(notify.subscribeToRemoteError))
				if (fromSubject) {
					socket.subjectSubscriber = Some(subscriber)
				}
			}

			session.on(`type` = "streamCreated", (e: Event) => {
				logger.debug("streamCreated")
				val se = e.asInstanceOf[StreamEvent]
				if (se.stream.name == controllerVideoStreamName) {
					subscribeToStream(se.stream, fromSubject = true)
				}
				else if (se.stream.name == adminVideoStreamName) {
					subscribeToStream(se.stream, fromSubject = false)
				}
			})

			session.on(`type` = "streamDestroyed", (e: Event) => {
				logger.debug("streamDestroyed")
				val se = e.asInstanceOf[StreamEvent]
				if (se.stream.name == controllerVideoStreamName) {
					socket.subjectSubscriber = None
					socket.setState(socket.state.copy(remoteVideo = false, remoteAudio = false))
				}
				else if (se.stream.name == adminVideoStreamName) {
					remoteAdminVideoElement.setSize(widthPx = 0, heightPx = 0)
				}
			})

			session.on("streamPropertyChanged", (e: Event) => {
				logger.debug("streamPropertyChanged")
				val se = e.asInstanceOf[StreamPropertyChangedEvent]
				if (se.stream.name == controllerVideoStreamName) {
					se.changedProperty match {
						case "hasAudio" =>
							logger.debug(s"hasAudio: ${se.stream.hasAudio}")
							socket.setState(socket.state.copy(remoteAudio = se.stream.hasAudio))
						case "hasVideo" =>
							logger.debug(s"hasVideo: ${se.stream.hasVideo}")
							socket.setState(socket.state.copy(remoteVideo = se.stream.hasVideo))
						case "videoDimensions" =>
							val dim = se.stream.videoDimensions
							val width = dim.width
							val height = dim.height
							logger.debug(s"dimensions: $width x $height")
							remoteVideoElement.adjustRatio(ratioWidth = width, ratioHeight = height)
						case _ =>
					}
				}
				else if ((se.stream.name == adminVideoStreamName) && socket.localVideoPublisher.isDefined) {
					socket.setState(socket.state.copy(localVideo = se.stream.hasVideo, localAudio = se.stream.hasAudio))
				}
			})

			session.on(`type` = controllerMessageTypeSignalString, (e: Event) => {
				val se = e.asInstanceOf[SignalEvent]
				logger.debug(s"OpenTok signal : ${se.`type`} : ${se.data}")
				notify.messageReceived(message = se.data, messageType = controllerMessageTypeString, source = "Controller")
			})

			session.on(`type` = adminChatMessageTypeSignalString, (e: Event) => {
				val se = e.asInstanceOf[SignalEvent]
				logger.debug(s"OpenTok signal : ${se.`type`} : ${se.data}")
				connectionId(se.from) foreach { metadata =>
					notify.messageReceived(message = se.data, messageType = adminChatMessageTypeString, source = metadata.user)
				}
			})

			session.on(`type` = "sessionDisconnected", (e: Event) => {
				val se = e.asInstanceOf[SessionDisconnectEvent]
				logger.debug(s"OpenTok sessionDisconnected: ${se.reason}")
				val error = se.reason match {
					case SessionDisconnectEvent.clientDisconnectedReason => None
					case SessionDisconnectEvent.forceDisconnectedReason => Some("A session administrator forced the disconnection.")
					case SessionDisconnectEvent.networkDisconnectedReason => Some("The connection to the session was lost.")
					case _ => Some(se.reason)
				}
				socket.setStatus(ChatConnectionStatus.Closed, error)
			})

			session.on(`type` = "connectionDestroyed", (e: Event) => {
				val ce = e.asInstanceOf[ConnectionEvent]
				connectionId(ce.connection).foreach(notify.remoteClientDisconnected)
			})

			session.on(`type` = "connectionCreated", (e: Event) => {
				val ce = e.asInstanceOf[ConnectionEvent]
				logger.debug(s"connectionCreated ${ce.connection.data}")
				for (
					data <- connectionId(ce.connection)
					if data != thisClient
				) notify.remoteClientConnected(data)
			})

			logger.debug("OpenTok connecting")
			logger.debug("token: " + tokenData.token)
			session.connect(tokenData.token, (e: js.UndefOr[Error]) => {
				e.toOption match {
					case Some(error) if (error != null) =>
						logger.debug(s"OpenTok connection failed: ${error.message}")
						socket.setStatus(ChatConnectionStatus.Closed, Some(error.message))
					case _ =>
						logger.debug(s"OpenTok connected")
						socket.setStatus(ChatConnectionStatus.Connected, None)
				}
			})

			socket
		} match {
			case Success(value) => Right(value)
			case Failure(e) => Left(e.getLocalizedMessage)
		}
	}
}